import { useEffect, useState } from "react";
import "./AddCategory.css"
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from "react-redux";
import { 
  UilTag, UilPlus, UilTrashAlt, UilGrids, UilListUl, 
  UilTrash, UilTimes, UilEdit, UilCheck, UilTimesCircle,
  UilSearch
} from "@iconscout/react-unicons";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
});

export const AddCategory = () => {
  const { currentUser } = useSelector((state) => state.auth);
  const [category, setCategory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("draft:add-category"))?.category || ""; } catch { return ""; }
  });
  const [subcategoryInput, setSubcategoryInput] = useState("");
  const [subCategories, setSubCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem("draft:add-category"))?.subCategories || []; } catch { return []; }
  });
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  
  // Edit mode states
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryTitle, setEditingCategoryTitle] = useState("");
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editingSubcategoryNewName, setEditingSubcategoryNewName] = useState("");
  const [addingSubcategoryTo, setAddingSubcategoryTo] = useState(null);
  const [newSubcategoryInput, setNewSubcategoryInput] = useState("");


  useEffect(() => {
    localStorage.setItem("draft:add-category", JSON.stringify({ category, subCategories }));
  }, [category, subCategories]);

  // â”€â”€ Sync token to axios after Redux rehydration â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const rawToken = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;
    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
      console.log(" Admin token synced to axios (AddCategory)");
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  const handleAddCategory = (e) => {
    setCategory(e.target.value)
  }

  const handleAddSubcategorie = () => {
    const trimmed = subcategoryInput.trim();
    if (trimmed && !subCategories?.includes(trimmed)) {
      setSubCategories([...subCategories, trimmed]);
      setSubcategoryInput("");
      toast.success(`Sous-catégorie "${trimmed}" ajoutée`, {
        position: "top-right",
        autoClose: 2000,
      });
    } else if (subCategories?.includes(trimmed)) {
      toast.warning("Cette sous-catégorie existe déjà", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const addCategory = async (title, subcategories) => {
    if (!title.trim()) {
      toast.error("Veuillez entrer un nom de catégorie", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post("/categories/", { 
        title, 
        subcategories 
      });

      console.log("Catégorie ajoutée :", response.data);
      toast.success('Catégorie ajoutée avec succès!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Reset form
      localStorage.removeItem("draft:add-category");
      setCategory("");
      setSubCategories([]);
      setSubcategoryInput("");
      
      // Refresh categories list
      loadCategories();
      
    } catch (error) {
      console.error("Erreur lors de l'ajout de la catégorie :", error);
      toast.error(error.response?.data?.message || "Erreur lors de l'ajout", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Start editing category
  const startEditCategory = (category) => {
    setEditingCategoryId(category._id);
    setEditingCategoryTitle(category.title);
  };
  
  // Cancel editing category
  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryTitle("");
  };
  
  // Save edited category
  const saveEditCategory = async (categoryId) => {
    if (!editingCategoryTitle.trim()) {
      toast.error("Le nom de la catégorie ne peut pas être vide", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    try {
      await api.put(`/categories/${categoryId}`, {
        title: editingCategoryTitle
      });
      
      toast.success("Catégorie modifiée avec succès", {
        position: "top-right",
        autoClose: 3000,
      });
      
      cancelEditCategory();
      loadCategories();
    } catch (error) {
      console.error("Erreur lors de la modification :", error);
      toast.error(error.response?.data?.message || "Erreur lors de la modification", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };
  
  // Start editing subcategory
  const startEditSubcategory = (categoryId, oldName) => {
    setEditingSubcategory({ categoryId, oldName });
    setEditingSubcategoryNewName(oldName);
  };
  
  // Cancel editing subcategory
  const cancelEditSubcategory = () => {
    setEditingSubcategory(null);
    setEditingSubcategoryNewName("");
  };
  
  // Save edited subcategory
  const saveEditSubcategory = async () => {
    if (!editingSubcategoryNewName.trim()) {
      toast.error("Le nom de la sous-catégorie ne peut pas être vide", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    try {
      await api.put(
        `/categories/${editingSubcategory.categoryId}/subcategory/${encodeURIComponent(editingSubcategory.oldName)}`,
        { newSubcategoryName: editingSubcategoryNewName.trim() }
      );
      
      toast.success(`Sous-catégorie renommée en "${editingSubcategoryNewName}"`, {
        position: "top-right",
        autoClose: 3000,
      });
      
      cancelEditSubcategory();
      loadCategories();
    } catch (error) {
      console.error("Erreur lors de la modification :", error);
      toast.error(error.response?.data?.message || "Erreur lors de la modification", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };
  
  // Start adding subcategory
  const startAddSubcategory = (categoryId) => {
    setAddingSubcategoryTo(categoryId);
    setNewSubcategoryInput("");
  };
  
  // Cancel adding subcategory
  const cancelAddSubcategory = () => {
    setAddingSubcategoryTo(null);
    setNewSubcategoryInput("");
  };
  
  // Save new subcategory
  const saveNewSubcategory = async (categoryId) => {
    if (!newSubcategoryInput.trim()) {
      toast.error("Le nom de la sous-catégorie ne peut pas être vide", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    try {
      await api.post(`/categories/${categoryId}/subcategory`, {
        subcategoryName: newSubcategoryInput.trim()
      });
      
      toast.success(`Sous-catégorie "${newSubcategoryInput}" ajoutée`, {
        position: "top-right",
        autoClose: 3000,
      });
      
      cancelAddSubcategory();
      loadCategories();
    } catch (error) {
      console.error("Erreur lors de l'ajout :", error);
      toast.error(error.response?.data?.message || "Erreur lors de l'ajout", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };
  
  // Delete entire category
  const handleDeleteCategory = async (categoryId, categoryTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la catégorie "${categoryTitle}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    setDeletingCategoryId(categoryId);
    
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success(`Catégorie "${categoryTitle}" supprimée avec succès`, {
        position: "top-right",
        autoClose: 3000,
      });
      loadCategories();
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error(error.response?.data?.message || "Erreur lors de la suppression", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setDeletingCategoryId(null);
    }
  };
  
  // Delete subcategory
  const handleDeleteSubcategory = async (categoryId, subcategoryName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la sous-catégorie "${subcategoryName}" ?`)) {
      return;
    }
    
    setDeletingSubcategory({ categoryId, subcategoryName });
    
    try {
      await api.delete(`/categories/${categoryId}/subcategory/${encodeURIComponent(subcategoryName)}`);
      toast.success(`Sous-catégorie "${subcategoryName}" supprimée avec succès`, {
        position: "top-right",
        autoClose: 3000,
      });
      loadCategories();
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error(error.response?.data?.message || "Erreur lors de la suppression", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setDeletingSubcategory(null);
    }
  };
  
  const loadCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data.categories);
      setFilteredCategories(response.data.categories);
    } catch (error) {
      console.error("Erreur de chargement des catégories", error);
      toast.error("Erreur lors du chargement des catégories", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // Filter categories based on search term (starts with, case insensitive)
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCategories(categories);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtered = categories.filter(cat => 
        cat.title.toLowerCase().startsWith(term)
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  const handleDeleteSubcategoryFromForm = (index) => {
    const updated = subCategories.filter((_, i) => i !== index);
    setSubCategories(updated);
    toast.info("Sous-catégorie supprimée du formulaire", {
      position: "top-right",
      autoClose: 1500,
    });
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="add-category-container">
      <div className="add-category-grid">
        {/* Left Column - Add Category Form */}
        <div className="add-category-grid-item form-section">
          
          {!currentUser && (
            <div className="auth-alert">
              ⚠️ Vous devez être connecté en tant qu'administrateur
            </div>
          )}
          
          <div className="form-group">
            <h4>Nom de la catégorie</h4>
            <div className="add-category-input">
              <UilTag className="input-icon" size={18} />
              <input 
                onChange={handleAddCategory}
                className="add-category-input-text" 
                placeholder="Ex: T-shirts, Sweats, Accessoires..."
                value={category}
              />
            </div>
            <div className="field-note">🏷️ Nom principal de la catégorie</div>
          </div>
          
          <div className="form-group">
            <h4>Sous-catégories</h4>
            <div className="add-category-tags">
              {subCategories?.map((subcategory, index) => (
                <span key={index} className="category-tag">
                  <UilListUl size={12} />
                  {subcategory}
                  <button
                    type="button"
                    onClick={() => handleDeleteSubcategoryFromForm(index)}
                    className="remove-tag-btn"
                    title="Supprimer cette sous-catégorie"
                  >
                    <UilTimes size={12} />
                  </button>
                </span>
              ))}
              {subCategories.length === 0 && (
                <div className="field-note" style={{ padding: "0.5rem 0", color: "var(--text-muted)" }}>
                  Aucune sous-catégorie ajoutée
                </div>
              )}
            </div>
            <div className="add-category-tags-input">
              <input
                type="text"
                placeholder="Ajouter une sous-catégorie"
                value={subcategoryInput}
                onChange={(e) => setSubcategoryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddSubcategorie();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddSubcategorie}
                className="add-subcategory-btn"
              >
                <UilPlus size={16} />
                Ajouter
              </button>
            </div>
            <div className="field-note">
              💡 Exemples: "Homme", "Femme", "Enfant", "Premium", "Basique"...
            </div>
          </div>
          
          <div className="category-publish-buttons">
            <button 
              onClick={() => addCategory(category, subCategories)} 
              className="submit-btn"
              disabled={loading || !category.trim()}
            >
              {loading ? "Envoi en cours..." : "📌 Publier la catégorie"}
            </button>
          </div>
        </div>
        
        {/* Right Column - Existing Categories List with Search */}
        <div className="add-category-grid-item list-section">
          <h2 className="section-title">
            <UilGrids size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Catégories existantes
          </h2>
          
          {/* Search Bar */}
          <div className="search-bar-container">
            <div className="search-input-wrapper">
              <UilSearch className="search-icon-category" size={18} />
              <input
                type="text"
                placeholder="Tapez la première lettre de la catégorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-category-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search-btn" title="Effacer la recherche">
                  <UilTimes size={16} />
                </button>
              )}
            </div>
          </div>
          
          {/* Search results info */}
          {searchTerm && (
            <div className="search-results-info">
              <UilSearch size={14} />
              <span>
                {filteredCategories.length} catégorie(s) commençant par "{searchTerm}"
              </span>
            </div>
          )}
          
          <div className="categories-list-container">
            {filteredCategories.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <UilTag size={64} />
                </div>
                {searchTerm ? (
                  <>
                    <p>Aucune catégorie commençant par "{searchTerm}"</p>
                    <div className="field-note">Essayez une autre lettre</div>
                  </>
                ) : (
                  <>
                    <p>Aucune catégorie disponible</p>
                    <div className="field-note">Créez votre première catégorie ci-contre</div>
                  </>
                )}
              </div>
            ) : (
              <ul className="categories-list">
                {filteredCategories.map((cat, index) => (
                  <li key={index} className="category-item">
                    <div className="category-header">
                      <UilTag className="category-icon" size={20} />
                      
                      {editingCategoryId === cat._id ? (
                        <div className="edit-category-container">
                          <input
                            type="text"
                            value={editingCategoryTitle}
                            onChange={(e) => setEditingCategoryTitle(e.target.value)}
                            className="edit-category-input"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEditCategory(cat._id);
                              if (e.key === "Escape") cancelEditCategory();
                            }}
                          />
                          <button
                            onClick={() => saveEditCategory(cat._id)}
                            className="save-edit-btn"
                            title="Enregistrer"
                          >
                            <UilCheck size={16} />
                          </button>
                          <button
                            onClick={cancelEditCategory}
                            className="cancel-edit-btn"
                            title="Annuler"
                          >
                            <UilTimesCircle size={16} />
                          </button>
                        </div>
                      ) : (
                        <strong className="category-title">{cat.title}</strong>
                      )}
                      
                      <div className="category-actions">
                        <span className="subcategory-count">
                          {cat.subcategories?.length || 0} sous-catégorie(s)
                        </span>
                        
                        {!editingCategoryId && (
                          <>
                            <button
                              onClick={() => startEditCategory(cat)}
                              className="edit-category-btn"
                              title="Modifier le nom de la catégorie"
                            >
                              <UilEdit size={16} />
                            </button>
                            <button
                              onClick={() => startAddSubcategory(cat._id)}
                              className="add-subcategory-to-btn"
                              title="Ajouter une sous-catégorie"
                            >
                              <UilPlus size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(cat._id, cat.title)}
                              className="delete-category-btn"
                              disabled={deletingCategoryId === cat._id}
                              title="Supprimer toute la catégorie"
                            >
                              <UilTrash size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Add new subcategory form */}
                    {addingSubcategoryTo === cat._id && (
                      <div className="add-subcategory-form">
                        <input
                          type="text"
                          placeholder="Nouvelle sous-catégorie"
                          value={newSubcategoryInput}
                          onChange={(e) => setNewSubcategoryInput(e.target.value)}
                          className="add-subcategory-input"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveNewSubcategory(cat._id);
                            if (e.key === "Escape") cancelAddSubcategory();
                          }}
                        />
                        <button
                          onClick={() => saveNewSubcategory(cat._id)}
                          className="save-subcategory-btn"
                        >
                          <UilCheck size={14} />
                          Ajouter
                        </button>
                        <button
                          onClick={cancelAddSubcategory}
                          className="cancel-subcategory-btn"
                        >
                          <UilTimes size={14} />
                          Annuler
                        </button>
                      </div>
                    )}
                    
                    {/* Subcategories list */}
                    {cat.subcategories?.length > 0 && (
                      <ul className="subcategories-list">
                        {cat.subcategories.map((sub, i) => {
                          const isEditing = editingSubcategory?.categoryId === cat._id && 
                                           editingSubcategory?.oldName === sub;
                          
                          return (
                            <li key={i} className="subcategory-item">
                              <span className="subcategory-dot"></span>
                              
                              {isEditing ? (
                                <div className="edit-subcategory-container">
                                  <input
                                    type="text"
                                    value={editingSubcategoryNewName}
                                    onChange={(e) => setEditingSubcategoryNewName(e.target.value)}
                                    className="edit-subcategory-input"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveEditSubcategory();
                                      if (e.key === "Escape") cancelEditSubcategory();
                                    }}
                                  />
                                  <button
                                    onClick={saveEditSubcategory}
                                    className="save-edit-subcategory-btn"
                                    title="Enregistrer"
                                  >
                                    <UilCheck size={14} />
                                  </button>
                                  <button
                                    onClick={cancelEditSubcategory}
                                    className="cancel-edit-subcategory-btn"
                                    title="Annuler"
                                  >
                                    <UilTimes size={14} />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <span className="subcategory-name">{sub}</span>
                                  <button
                                    onClick={() => startEditSubcategory(cat._id, sub)}
                                    className="edit-subcategory-btn"
                                    title="Modifier cette sous-catégorie"
                                  >
                                    <UilEdit size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubcategory(cat._id, sub)}
                                    className="delete-subcategory-btn"
                                    disabled={
                                      deletingSubcategory?.categoryId === cat._id && 
                                      deletingSubcategory?.subcategoryName === sub
                                    }
                                    title="Supprimer cette sous-catégorie"
                                  >
                                    <UilTrashAlt size={14} />
                                    <span className="delete-text">Supprimer</span>
                                  </button>
                                </>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  )
}
